import { useLanguage } from "../../../../context/LanguageContext";
import { Button } from "../../../../components/ui/button";
import {
  ArrowUpDown,
  Check,
  CreditCard,
  Eye,
  Loader2,
  PlusCircle,
} from "lucide-react";
import { DataTable } from "../../../../components/DataTable";
import { useAuth } from "../../../../context/AuthContext";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../../lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Spin, Tooltip } from "antd";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { DateFormate } from "./../../../../lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";

const SalaryPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [salaryData, setSalaryData] = useState({});
  const [errors, setErrors] = useState({});

  const [salary, setSalary] = useState([]);
  const [loading, setLoading] = useState(true);

  const ApiUrl =
    user?.role === "admin"
      ? `teacher-payments`
      : user?.role === "teacher" && `teacher-payments/teacher/${user?.id}`;

  const fetchSalary = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/${ApiUrl}`);
      setSalary(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalary();
  }, []);

  const columns = [
    {
      accessorKey: "teacher_id.name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Teacher Name
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
    },
    {
      accessorKey: "month",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Month
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
    },
    {
      accessorKey: "lesson_num",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Lessons
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
    },
    {
      accessorKey: "total_amount",
      header: "Total Amount",
    },
    {
      accessorKey: "teacher_amount",
      header: "Teacher Amount",
    },
    {
      accessorKey: "site_amount",
      header: "Site Amount",
    },
    {
      id: "hold_amount",
      accessorFn: (row) => row.hold?.total_hold_amount ?? "0.00",
      header: "Hold Amount",
    },
    {
      accessorKey: "net_salary",
      header: "Net Salary",
    },
    {
      accessorKey: "payment_date",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Payment Date
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ getValue }) => DateFormate(getValue()) ?? "—",
    },
  ];

  const handleChoosePaymentMethod = async (formData) => {
    setLoading(true);
    try {
      await axios.put(
        `${BaseUrlApi}/teacher-payments/${salaryData.id}`,
        formData
      );
      setOpen(false);
      toast("Success", { description: "Salary is updated" });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
      setErrors(result);
    } finally {
      setLoading(false);
    }
  };

  if (user?.role === "admin") {
    columns.push({
      id: "actions",
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Tooltip
            title={
              !!row.original.payment_date
                ? "Salary is payed ✔"
                : "Select Payment Method"
            }
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (!!!row.original.payment_date) {
                  setSalaryData(row.original);
                  setOpen(true);
                }
              }}
            >
              <CreditCard className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      ),
    });
  }

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <h1 className="text-2xl font-bold">Salary</h1>
        <PayDialog
          open={open}
          onOpenChange={setOpen}
          data={salaryData}
          saving={loading}
          setSaving={setLoading}
          onSave={handleChoosePaymentMethod}
          errors={errors}
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={salary}
          title={"salary by month & name"}
          filterNames={["month", "teacher_id_name"]}
        />
      )}
    </div>
  );
};

export default SalaryPage;

function PayDialog({
  open,
  onOpenChange,
  data,
  onSave,
  saving,
  setSaving,
  errors,
}) {
  const { language } = useLanguage();

  const [formData, setFormData] = useState({
    payment_method_id: null,
  });

  useEffect(() => {
    if (data)
      setFormData({
        payment_method_id: data?.payment_method_id,
      });
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchPaymentMethods = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/payment-methods`);
      setPaymentMethods(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[450px] max-h-[90vh] overflow-auto"
        dir={language === "en" ? "ltr" : "rtl"}
      >
        <DialogHeader>
          <DialogTitle>{"Select Payment Methods"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Role */}
            <div className="grid">
              <Select
                id="role"
                name="role"
                disabled={loading}
                value={+formData.payment_method_id || ""}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    payment_method_id: +value,
                  }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a methods" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.length !== 0 ? (
                    paymentMethods.map((method) => (
                      <SelectItem key={method.id} value={method.id}>
                        {method.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem>No Payment Methods Exist !</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.payment_method_id && (
                <p className="text-red-500 text-sm">
                  {errors.payment_method_id}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className={"flex gap-2 justify-between flex-wrap"}>
            <DialogClose>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-fit"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {"Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
